import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const fmtSEK = (v) => (v == null ? "—" : new Intl.NumberFormat("sv-SE", { maximumFractionDigits: 0 }).format(Math.round(v)));
const fmtSigned = (v) => (v == null ? "—" : `${v < 0 ? "−" : v > 0 ? "+" : ""}${fmtSEK(Math.abs(v))}`);
const g = (obj, m) => obj?.[m] ?? obj?.[String(m)];

// Build 8 — Group Overhead workspace (§39-42): gross overhead, the single
// Management Adjustment mechanism, headcount allocation key, allocations,
// retained share, ratio vs target, and Finance-controlled group settings.
export default function GroupOverheadPage() {
  const { user, isAdmin } = useAuth();
  const canEdit = isAdmin || user?.role === "COMPANY_MANAGER";
  const { year, setYear } = useActiveCompany();
  const [years, setYears] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noGroup, setNoGroup] = useState(false);
  const [paymentTerms, setPaymentTerms] = useState([]);
  const [vatRates, setVatRates] = useState([]);

  const [adjAmount, setAdjAmount] = useState("");
  const [adjComment, setAdjComment] = useState("");
  const [settingsDraft, setSettingsDraft] = useState({ overhead_payment_terms_id: "", intercompany_vat_rate_id: "" });
  const [hubId, setHubId] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/companies"), api.get("/payment-terms"), api.get("/vat/rates")])
      .then(async ([c, t, v]) => {
        setPaymentTerms(t.data.filter((x) => x.active !== false));
        setVatRates(v.data.filter((x) => x.active !== false));
        const hub = c.data.find((x) => x.is_group_company);
        if (!hub) { setNoGroup(true); return; }
        setHubId(hub.id);
        const yrs = await api.get(`/companies/${hub.id}/periods/years`);
        setYears(yrs.data);
        const pick = pickActiveYear(yrs.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    if (!year) return;
    setLoading(true);
    try {
      const res = await api.get("/group/overhead", { params: { year } });
      setData(res.data);
      setSettingsDraft({
        overhead_payment_terms_id: res.data.settings.overhead_payment_terms_id || "",
        intercompany_vat_rate_id: res.data.settings.intercompany_vat_rate_id || "",
      });
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load().catch(() => {});
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const m = g(data?.months, month);
    setAdjAmount(m && m.adjustment !== 0 ? String(m.adjustment) : "");
    setAdjComment(m?.adjustment_comment || "");
  }, [data, month]);

  const saveAdjustment = async () => {
    try {
      await api.put("/group/overhead/adjustments", {
        company_id: hubId,
        year,
        month,
        amount: adjAmount === "" ? 0 : Number(adjAmount),
        comment: adjComment || null,
      });
      toast.success("Management adjustment saved");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveSettings = async () => {
    try {
      await api.put("/group/settings", {
        company_id: hubId,
        overhead_payment_terms_id: settingsDraft.overhead_payment_terms_id || null,
        intercompany_vat_rate_id: settingsDraft.intercompany_vat_rate_id || null,
      });
      toast.success("Group settings saved");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const m = g(data?.months, month);
  const fy = data?.fy;

  return (
    <div data-testid="group-overhead-page">
      <PageHeader
        title="Group Overhead"
        subtitle={data ? `${data.hub.name} — allocation to legal entities by consultant headcount` : "Group overhead allocation"}
        actions={
          <div className="flex items-center gap-2">
            {years.length > 0 && (
              <Select value={year ? String(year) : ""} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger className="w-28" data-testid="goh-year-select"><SelectValue placeholder="Year" /></SelectTrigger>
                <SelectContent>
                  {years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`goh-year-${y}`}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-32" data-testid="goh-month-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {MONTHS.map((label, i) => <SelectItem key={i + 1} value={String(i + 1)} data-testid={`goh-month-${i + 1}`}>{label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        }
      />

      {noGroup && (
        <div className="bg-white border border-slate-200 rounded-lg">
          <EmptyState title="No Group company configured" description="Mark a company as the Group company under Administration → Companies." testId="goh-no-group" />
        </div>
      )}
      {loading && <div className="py-16 text-center text-sm text-slate-400" data-testid="goh-loading">Loading…</div>}

      {!loading && data && m && (
        <>
          {(data.warnings || []).length > 0 && (
            <div className="mb-4 space-y-1.5" data-testid="goh-warnings">
              {(data.warnings || []).map((w, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-xs text-[#92400E]">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {w}
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4" data-testid="goh-summary">
            {[
              { label: "Gross Group Overhead", value: fmtSEK(m.gross_overhead), testId: "goh-gross" },
              { label: "Management Adjustment", value: fmtSigned(m.adjustment), testId: "goh-adjustment" },
              { label: "Overhead to Allocate", value: fmtSEK(m.overhead_to_allocate), testId: "goh-to-allocate" },
              { label: "Allocated to Operating Cos", value: fmtSEK(m.allocated_to_operating), testId: "goh-allocated" },
              { label: "Retained by Group", value: fmtSEK(m.retained_by_group), testId: "goh-retained" },
              {
                label: `Group Overhead Ratio (target ${m.target ?? "—"}%)`,
                value: m.overhead_ratio == null ? "N/A" : `${m.overhead_ratio}%`,
                testId: "goh-ratio",
              },
            ].map((c) => (
              <div key={c.label} className="bg-white border border-slate-200 rounded-lg p-3">
                <div className="text-[11px] text-slate-500 leading-tight">{c.label}</div>
                <div className="text-lg font-bold text-slate-900 mt-1" data-testid={c.testId}>{c.value}</div>
                {fy && c.testId === "goh-ratio" && (
                  <div className="text-[10px] text-slate-400 mt-0.5" data-testid="goh-ratio-fy">FY: {fy.overhead_ratio == null ? "N/A" : `${fy.overhead_ratio}%`}</div>
                )}
              </div>
            ))}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg mb-4 px-4 py-3" data-testid="goh-pool-composition">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Pool composition — {MONTHS[month - 1]} {year}</div>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-sm text-slate-700">
              <span data-testid="goh-pc-personnel">Overhead Personnel <strong>{fmtSEK(m.overhead_personnel_cost)}</strong></span>
              <span className="text-slate-400">+</span>
              <span data-testid="goh-pc-regular">Regular Indirect <strong>{fmtSEK(m.regular_indirect)}</strong></span>
              {m.regular_indirect_excluded ? (
                <>
                  <span className="text-slate-400">−</span>
                  <span className="text-slate-600" data-testid="goh-pc-excluded">Excluded from Group Overhead <strong>{fmtSEK(m.regular_indirect_excluded)}</strong></span>
                </>
              ) : null}
              <span className="text-slate-400">=</span>
              <span data-testid="goh-pc-gross">Gross Group Overhead <strong>{fmtSEK(m.gross_overhead)}</strong></span>
              <span className="text-slate-400">+</span>
              <span data-testid="goh-pc-adjustment">Management Adjustment <strong>{fmtSigned(m.adjustment)}</strong></span>
              <span className="text-slate-400">=</span>
              <span className="text-slate-900" data-testid="goh-pc-to-allocate">Amount to Allocate <strong>{fmtSEK(m.overhead_to_allocate)}</strong> kr</span>
            </div>
            {m.regular_indirect_excluded ? (
              <p className="text-[11px] text-slate-400 mt-1" data-testid="goh-pc-excluded-note">
                Excluded accounts remain real {data.hub.name} costs — simply not distributed. Eligibility is maintained under Administration → Cost Accounts.
              </p>
            ) : null}
          </div>

          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4" data-testid="goh-allocation-table">
            <table className="w-full">
              <thead>
                <tr className="bg-secondary/50 border-b border-slate-200">
                  {["Company", "Eligible Consultant Headcount", "Allocation Key", "Allocated Amount", "Status"].map((h) => (
                    <th key={h} className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.companies.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-accent/40" data-testid={`goh-row-${c.id}`}>
                    <td className="py-2.5 px-4 text-sm font-semibold text-slate-800">{c.name}</td>
                    <td className="py-2.5 px-4 text-sm text-slate-700" data-testid={`goh-hc-${c.id}`}>{m.headcounts[c.id] ?? 0}</td>
                    <td className="py-2.5 px-4 text-sm text-slate-700" data-testid={`goh-key-${c.id}`}>
                      {m.allocation_keys[c.id] != null ? `${m.allocation_keys[c.id]}%` : "N/A"}
                    </td>
                    <td className="py-2.5 px-4 text-sm font-semibold text-slate-900" data-testid={`goh-alloc-${c.id}`}>
                      {m.allocations[c.id] != null ? `${fmtSEK(m.allocations[c.id])} kr` : "—"}
                    </td>
                    <td className="py-2.5 px-4">
                      {c.is_hub ? (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#0047B3]">Retained</span>
                      ) : (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#ECFDF5] text-[#047857]">Generated (6591)</span>
                      )}
                    </td>
                  </tr>
                ))}
                <tr className="bg-secondary/50 border-t border-slate-200" data-testid="goh-total-row">
                  <td className="py-2.5 px-4 text-sm font-bold text-slate-900">Total</td>
                  <td className="py-2.5 px-4 text-sm font-bold text-slate-900" data-testid="goh-total-hc">{m.total_headcount}</td>
                  <td className="py-2.5 px-4 text-sm font-bold text-slate-900">
                    {m.total_headcount ? `${Object.values(m.allocation_keys).reduce((a, b) => a + b, 0).toFixed(2).replace(/\.?0+$/, "")}%` : "N/A"}
                  </td>
                  <td className="py-2.5 px-4 text-sm font-bold text-slate-900" data-testid="goh-total-alloc">{fmtSEK(m.overhead_to_allocate)} kr</td>
                  <td className="py-2.5 px-4 text-xs text-slate-500">Allocated + retained reconcile</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="goh-adjustment-card">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Management Adjustment — {MONTHS[month - 1]} {year}</h3>
              <p className="text-xs text-slate-500 mb-3">
                Adjusts what is distributed, never the underlying Group cost. Comment required for non-zero amounts.
              </p>
              <div className="text-xs text-slate-600 mb-3 bg-secondary/50 border border-slate-200 rounded-md px-3 py-2" data-testid="goh-adj-preview">
                Gross: <strong>{fmtSEK(m.gross_overhead)}</strong> kr → Adjustment: <strong>{fmtSigned(adjAmount === "" ? 0 : Number(adjAmount))}</strong> kr → Net to allocate:{" "}
                <strong>{fmtSEK(m.gross_overhead + (adjAmount === "" ? 0 : Number(adjAmount)))} kr</strong>
              </div>
              <div className="flex gap-3">
                <div className="w-40">
                  <Label className="text-xs">Amount (SEK, signed)</Label>
                  <Input type="number" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)} disabled={!canEdit} placeholder="-50000" data-testid="goh-adj-amount" />
                </div>
                <div className="flex-1">
                  <Label className="text-xs">Comment</Label>
                  <Input value={adjComment} onChange={(e) => setAdjComment(e.target.value)} disabled={!canEdit} placeholder="Reason for the adjustment" data-testid="goh-adj-comment" />
                </div>
              </div>
              {canEdit && (
                <Button size="sm" className="mt-3 bg-[#0052CC] hover:bg-[#0047B3]" onClick={saveAdjustment} data-testid="goh-adj-save">
                  Save adjustment
                </Button>
              )}
            </div>

            <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="goh-settings-card">
              <h3 className="text-sm font-semibold text-slate-900 mb-1">Group settings</h3>
              <p className="text-xs text-slate-500 mb-3">
                Finance-controlled intercompany assumptions. Missing configuration surfaces explicit warnings — never silent defaults.
              </p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Group Overhead payment terms</Label>
                  <Select
                    value={settingsDraft.overhead_payment_terms_id || "none"}
                    onValueChange={(v) => setSettingsDraft({ ...settingsDraft, overhead_payment_terms_id: v === "none" ? "" : v })}
                    disabled={!canEdit}
                  >
                    <SelectTrigger data-testid="goh-terms-select"><SelectValue placeholder="Not configured" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not configured</SelectItem>
                      {paymentTerms.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`goh-terms-${t.days}`}>{t.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Intercompany VAT rate</Label>
                  <Select
                    value={settingsDraft.intercompany_vat_rate_id || "none"}
                    onValueChange={(v) => setSettingsDraft({ ...settingsDraft, intercompany_vat_rate_id: v === "none" ? "" : v })}
                    disabled={!canEdit}
                  >
                    <SelectTrigger data-testid="goh-vat-select"><SelectValue placeholder="Not configured" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Not configured</SelectItem>
                      {vatRates.map((v) => <SelectItem key={v.id} value={v.id} data-testid={`goh-vat-${v.rate}`}>{v.name} ({v.rate}%)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {canEdit && (
                  <Button size="sm" className="bg-[#0052CC] hover:bg-[#0047B3]" onClick={saveSettings} data-testid="goh-settings-save">
                    Save settings
                  </Button>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
